import { Component, ElementRef, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { Router, NavigationEnd } from '@angular/router';
import { AppRoutingService } from '@services/app-routing/app-routing.service';
import { QuestionnaireAction, QuestionnaireState } from '@state/questionnaire.state';
import { UserAction, UserState, UserStatus } from '@state/user.state';
import { CustomValidators } from 'app/main/helpers/custom-validators';
import {
    Observable,
    ReplaySubject,
    Subject,
    catchError,
    combineLatest,
    from,
    mergeMap,
    of,
    startWith,
    takeUntil,
    toArray,
} from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { filter } from 'rxjs/operators';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { City } from 'app/@types/shared/world-cities';
import countryData from '@assets/data/countries.json';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { ToastService } from '@services/common/toast.service';
import { CognitoAuthenticationService } from 'app/auth/service';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { OrganizationAction, OrganizationState } from '@state/organization.state';
import { DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { ConfigService } from 'app/app-config.service';
import { MenuItem } from 'primeng/api';
import { SelectChangeEvent } from 'primeng/select';
import { FormulaireCallCompletion } from 'app/@types/shared/formulaire-call-completion';
import { UserHttpService } from '@services/datasources/user-http.service';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { UserInfoDto } from 'app/@types/dto/user-info-dto.model';
import { QuestionnaireHttpService } from '@services/datasources/questionnaire-http.service';
import { CurrencyOption } from '../settings-form/settings-form-entities/edit-entity/edit-entity.component';
import { IndustryDto } from 'app/@types/dto/industry-dto.model';
import { IndustryService } from '@services/common/industry.service';
import { buildCountryOptions, CountryOption } from 'app/main/helpers/country-options';
import { isRequired, revealFormErrors } from 'app/main/helpers/form-validation';

@Component({
    selector: 'app-onboarding',
    templateUrl: './onboarding.component.html',
    styleUrls: ['./onboarding.component.scss'],
    standalone: false,
})
export class OnboardingComponent implements OnInit {
    @Select(UserState.getCurrentUserClaims) userClaims$!: Observable<UserClaims | undefined>;
    @Select(UserState.getUserStatus) userStatus$!: Observable<UserStatus | undefined>;
    @Select(QuestionnaireState.getQuestionnaire) questionnaire$!: Observable<QuestionnaireDto>;
    @Select(OrganizationState.getEntities) entities$!: Observable<OrganizationEntityDto[]>;
    config = this.configService.getConfig;
    mainColor: string = this.configService.brand.primary;

    public forms!: FormGroup;
    public countries: CountryOption[] = [];
    public filteredCities: City[] | undefined;
    public userClaim: UserClaims = {
        user_email: '',
        entities_count: 0,
        max_entities: 0,
        tasks: [],
    };
    public questionnaire: QuestionnaireDto = {
        organizationEntityId: '',
        deleted: false,
        isLocked: false,
        lastUpdate: '',
        visitedPages: [],
        refYear: 0,
        updatedBy: '',
        totalCO2e: 0,
        period: 0,
        periodicity: 0,
        measureType: 0,
        country: '',
        creationDateTime: '',
        typeId: 0,
        questionnaireDimensions: [],
    };
    public loading: boolean = false;
    public isOnboarding: boolean = true;
    public vatPlaceholder = 'BE0123.456.789';
    public currentStep = 0;
    public periodList: number[] = [];
    public lang: string = 'en';
    public currencyOptions: CurrencyOption[] = [];

    public steps: MenuItem[] = [];

    public industries: IndustryDto[] = [];
    private industries$ = this.industryService.get();

    protected readonly isRequired = isRequired;

    private ngUnsubscribe = new Subject<void>();
    constructor(
        public configService: ConfigService,
        public appRoutingService: AppRoutingService,
        private formBuilder: FormBuilder,
        private store: Store,
        private authService: CognitoAuthenticationService,
        private toastService: ToastService,
        private translateService: CustomTranslateService,
        public ref: DynamicDialogRef,
        private userService: UserHttpService,
        private router: Router,
        private dialogService: DialogService,
        private questionnaireService: QuestionnaireHttpService,
        private readonly industryService: IndustryService,
        private readonly host: ElementRef<HTMLElement>,
    ) {
        const currentYear = new Date().getFullYear();
        this.periodList = Array.from({ length: 5 }, (_, i) => currentYear - i);

        this.forms = this.formBuilder.group({
            userGroup: this.formBuilder.group({
                lastname: new FormControl('', Validators.required),
                firstname: new FormControl('', Validators.required),
                email: new FormControl({ value: '', disabled: true }, Validators.required),
                phoneNumber: new FormControl(null, CustomValidators.phoneNumberValidator),
                role: new FormControl(''),
            }),
            organizationGroup: this.formBuilder.group({
                companyName: new FormControl('', Validators.required),
                vatNumber: new FormControl(''),
                naceCode: new FormControl(''),
                industryCode: new FormControl(''),
                companyLogo: new FormControl(''),
            }),

            entityGroup: this.formBuilder.group({
                id: new FormControl(''),
                name: new FormControl('', Validators.required),
                country: new FormControl('', Validators.required),
                refYear: new FormControl(1),
                annualRevenue: new FormControl(0),
                fte: new FormControl(1),
                currency: new FormControl('', Validators.required),
            }),
        });
    }

    ngOnInit(): void {
        combineLatest([
            this.userClaims$,
            this.translateService.onLangChange.pipe(startWith(null)),
            this.entities$,
            this.questionnaire$,
            this.industries$,
        ])
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(([uc, _lang, entities, quest, industries]) => {
                this.industries = industries
                    .map((item) => ({
                        ...item,
                        label: this.translateService.instant(
                            `components.settingsForm.industry.${item.industrySectorLabel}`,
                        ),
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));
                this.translateService.get('countries').subscribe((countries) => {
                    this.countries = buildCountryOptions(countries, this.translateService.currentLang);
                });

                if (uc?.organization && uc.organization.uid && quest) {
                    this.questionnaire = quest;
                    this.entityFormGroup.patchValue({ refYear: quest.refYear });
                    this.userClaim = uc;
                    this.organizationFormGroup.enable();
                    this.setFormValuesFromUserClaims();
                    this.setUserFormValuesFromOrganization(uc.organization);

                    this.questionnaireService.getCurrencies(uc.organization.uid).subscribe((currencies) => {
                        this.currencyOptions = currencies.map((currency) => {
                            return {
                                code: currency.code,
                                label: this.translateService.instant(`common.currency.${currency.code}`),
                            };
                        });
                    });
                } else {
                    this.organizationFormGroup.disable();
                }
                if (entities && entities.length > 0 && this.questionnaire) {
                    this.setFormValuesFromEntity(entities[0]);
                }
                this.steps = [
                    { label: this.translateService.instant('home.onboarding.stepper.welcome') },
                    { label: this.translateService.instant('home.onboarding.stepper.user') },
                    { label: this.translateService.instant('home.onboarding.stepper.organization') },
                    { label: this.translateService.instant('home.onboarding.stepper.entity') },
                ];
                this.lang = this.translateService.currentLang;
            });

        this.router.events
            .pipe(
                // Filter events to ensure we only process NavigationEnd events
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            )
            .subscribe((event) => {
                // Check if the new route starts with "/dedicated_admin"
                // This means the user is navigating to the admin dashboard
                if (event.url.startsWith('/dedicated_admin')) {
                    this.closeAllDialogs(); // Close all open dialogs
                }
            });
    }

    setFormValuesFromUserClaims(): void {
        const { organization } = this.userClaim;

        this.organizationFormGroup.patchValue({
            companyName: organization?.name,
            vatNumber: organization?.tva,
            naceCode: organization?.nace,
            companyLogo: organization?.avatar,
            industryCode: organization?.industryId,
        });
        this.entityFormGroup.patchValue({
            country: organization?.country,
        });
        // VAT format hint from the org's country, matching the settings/creation forms. Onboarding
        // enters the VAT before the country step and never re-validates it, so this is a hint only;
        // onEntityCountryChange keeps it in sync if the country is edited on the entity step.
        this.vatPlaceholder =
            countryData.find((c) => c.id === organization?.country)?.placeholderVAT ?? 'BE0123.456.789';

        this.organizationFormGroup.markAsPristine();
    }

    // Keeps the VAT format hint in sync when the entity country changes, so it is still correct if
    // the user edits the country and steps back to the VAT field. Hint only — onboarding does not
    // validate the VAT (its country is chosen on a later step and can change afterwards).
    onEntityCountryChange(event: SelectChangeEvent): void {
        this.vatPlaceholder = countryData.find((c) => c.id === event.value)?.placeholderVAT ?? 'BE0123.456.789';
    }

    setFormValuesFromEntity(entity: OrganizationEntityDto): void {
        this.entityFormGroup.patchValue({
            id: entity.id ?? '',
            name: entity.name,
            country: entity.country,
            revenues: entity.annualRevenue,
            fte: entity.employeesCount,
            refYear: this.questionnaire?.refYear,
            annualRevenue: entity.annualRevenue,
            currency: entity.currency,
        });
    }

    setUserFormValuesFromOrganization(org: OrganizationDto): void {
        this.userFormGroup.disable();
        if (org.uid) {
            this.userService.getUserByOrganization(org.uid).subscribe((users) => {
                const orgOwner = users.find((user) =>
                    user.roles.some((role) => role.organization_uid === org.uid && role.role_name === 'main_user'),
                );
                if (orgOwner) {
                    this.userFormGroup.patchValue({
                        lastname: orgOwner.lastname || '',
                        firstname: orgOwner.firstname || '',
                        role: orgOwner.role || '',
                        email: orgOwner.email,
                        phoneNumber: orgOwner.phone_number !== '+3200000000000' ? orgOwner.phone_number : null,
                    });
                    this.userFormGroup.enable();
                    this.userFormGroup.get('email')?.disable();
                }
            });
        }
    }

    onSubmit(): void {
        if (this.loading) {
            return;
        } //Prevent user to launch the submit multiple time in a row
        if (!revealFormErrors(this.forms, this.host.nativeElement)) {
            return;
        }
        this.loading = true;
        const organization = this.userClaim.organization;

        // Initialize completion signals
        const completionSignals: Subject<string>[] = [];

        const orgUpdateResult = new ReplaySubject<string>(1);
        const userUpdateResult = new ReplaySubject<string>(1);
        const entityUpdateResult = new ReplaySubject<string>(1);
        const updateActions: Observable<string>[] = [];

        // Update organization
        if (
            this.organizationFormGroup.valid &&
            this.userClaim.organization &&
            (!this.organizationFormGroup.pristine || this.isOnboarding)
        ) {
            const updatedOrg = this.userClaim.organization;
            updatedOrg.country = this.entityFormGroup.get('country')?.value as string;
            updatedOrg.name = this.organizationFormGroup.get('companyName')?.value as string;
            updatedOrg.tva = this.organizationFormGroup.get('vatNumber')?.value as string;
            updatedOrg.avatar = this.organizationFormGroup.get('companyLogo')?.value as string;
            updatedOrg.industryId = this.organizationFormGroup.get('industryCode')?.value as string;
            updatedOrg.isOnboarded = true;
            completionSignals.push(orgUpdateResult);
            updateActions.push(
                this.store.dispatch(new UserAction.UpdateOrganization(updatedOrg, false)).pipe(
                    mergeMap(() => {
                        this.organizationFormGroup.markAsPristine();
                        orgUpdateResult.next('orgSuccess');
                        orgUpdateResult.complete();
                        this.toastService.showSuccess({
                            details: 'components.settingsForm.companySection.toasts.updateSuccess',
                        });
                        return of('orgSuccess');
                    }),
                    catchError(() => {
                        orgUpdateResult.next('orgFail');
                        orgUpdateResult.complete();
                        this.toastService.showError({
                            details: 'components.settingsForm.companySection.toasts.updateError',
                        });
                        return of('orgFail');
                    }),
                ),
            );
        }

        // Update user
        if (this.userFormGroup.valid && (!this.userFormGroup.pristine || this.isOnboarding)) {
            completionSignals.push(userUpdateResult);
            const user: UserInfoDto = {
                email: this.userFormGroup.get('email')?.value as string,
                phone_number: CustomValidators.toE164Phone(this.userFormGroup.get('phoneNumber')?.value as string),
                role: this.userFormGroup.get('role')?.value as string,
                firstname: this.userFormGroup.get('firstname')?.value as string,
                lastname: this.userFormGroup.get('lastname')?.value as string,
                roles: [],
            };

            updateActions.push(
                this.userService.updateCognitoUser(user).pipe(
                    mergeMap(() => this.authService.refreshAuthenticatedUser()),
                    mergeMap(() => {
                        userUpdateResult.next('userSuccess');
                        userUpdateResult.complete();
                        return of('userSuccess');
                    }),
                    catchError(() => {
                        userUpdateResult.next('userFail');
                        userUpdateResult.complete();
                        this.toastService.showError({
                            details: 'components.settingsForm.userSection.toasts.updateError',
                        });
                        return of('userFail');
                    }),
                ),
            );
        }

        // Update entity
        if (this.entityFormGroup.valid) {
            const entity = {
                organizationId: organization?.uid ?? '',
                ...this.entityFormGroup.value,
                employeesCount: this.entityFormGroup.get('fte')?.value as number,
                isActive: true,
                deleted: false,
                industryId: this.organizationFormGroup.get('industryCode')?.value as string,
                currency: this.entityFormGroup.get('currency')?.value as string,
                annualRevenue: this.entityFormGroup.get('annualRevenue')?.value as number,
            } as OrganizationEntityDto;
            const questionnaire = this.questionnaire;
            questionnaire.refYear = this.entityFormGroup.get('refYear')?.value as number;

            completionSignals.push(entityUpdateResult);

            const callCompletion: Subject<OrganizationEntityDto | undefined> = new Subject();
            const questionnaireUpdateResult = new Subject<FormulaireCallCompletion>();

            if (organization?.uid) {
                this.store.dispatch(
                    new QuestionnaireAction.UpdatePropertyAction(
                        organization.uid,
                        questionnaire,
                        questionnaireUpdateResult,
                    ),
                );
            }
            if (entity.id) {
                updateActions.push(
                    this.store
                        .dispatch(
                            new OrganizationAction.UpdateEntitieAction(
                                entity.organizationId,
                                entity.id,
                                entity,
                                callCompletion,
                            ),
                        )
                        .pipe(
                            mergeMap(() => {
                                entityUpdateResult.next('entitySuccess');
                                this.toastService.showSuccess({
                                    details: 'components.settingsForm.entitySection.toasts.updateSuccess',
                                });
                                return of('entitySuccess');
                            }),
                            catchError(() => {
                                entityUpdateResult.next('entityFail');
                                this.toastService.showError({
                                    details: 'components.settingsForm.entitySection.toasts.updateError',
                                });
                                return of('entityFail');
                            }),
                        ),
                );
            }
        }

        // Handle all completion signals

        from(updateActions)
            .pipe(
                mergeMap((action) => action),
                toArray(),
            )
            .subscribe({
                next: (results) => {
                    if (results.includes('orgFail') || results.includes('userFail') || results.includes('entityFail')) {
                        // this.toastService.showError({ details: 'components.settingsForm.updateError' });
                    } else {
                        this.ref.close();
                        const element = document.getElementById('language-selector');
                        if (element) {
                            element.style.display = 'block';
                        }
                        this.ref.destroy();
                        this.appRoutingService.onOnboarded();
                    }
                },
                complete: () => {
                    this.loading = false;
                },
            });
    }

    nextStep(): void {
        if (this.currentStep < 3) {
            const form =
                this.currentStep === 0
                    ? undefined
                    : this.currentStep === 1
                      ? this.userFormGroup
                      : this.currentStep === 2
                        ? this.organizationFormGroup
                        : this.entityFormGroup;
            // A step still disabled (its data loading) is neither valid nor invalid: it lets the user on.
            if (form?.invalid) {
                revealFormErrors(form, this.host.nativeElement);
                return;
            }
            this.currentStep++;
        }
    }

    previousStep(): void {
        if (this.currentStep > 0) {
            this.currentStep--;
        }
    }

    logout(): void {
        this.authService.logout(true).subscribe();
        this.ref.close();
    }

    public sendEmail() {
        const mailTo = this.configService.getClient.support?.mailDcarbonize;
        const mail = `mailto:${mailTo}`;
        window.open(mail, 'emailWindow');
    }

    get organizationFormGroup(): FormGroup {
        return this.forms.get('organizationGroup') as FormGroup;
    }

    get userFormGroup(): FormGroup {
        return this.forms.get('userGroup') as FormGroup;
    }

    get entityFormGroup(): FormGroup {
        return this.forms.get('entityGroup') as FormGroup;
    }

    setStep(index: number): void {
        this.currentStep = index;
    }

    clearInput(fc: AbstractControl | null): void {
        if (fc) {
            fc.setValue('');
        }
    }

    /**
     * Closes all currently open PrimeNG dialogs.
     * This method iterates over all dialog instances in the `dialogComponentRefMap`
     * and calls `close()` on each to ensure they are properly dismissed.
     */
    private closeAllDialogs(): void {
        this.dialogService.dialogComponentRefMap.forEach((dialogRef) => {
            dialogRef.instance.close(); // Close each active dialog instance
        });
    }
}
